import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { OrderTicket } from "./components/OrderTicket";
import PortfolioView from "./components/PortfolioView";
import PoolWizard from "./components/PoolWizard";
import { EditProfileModal } from "./components/EditProfileModal";
import MarketTable from "./components/MarketTable";
import StyleGuide from "./components/StyleGuide";
import { AppHeader } from "./components/layout/AppHeader";
import { AppFooter } from "./components/layout/AppFooter";
import { AuthModal } from "./components/AuthModal";
import { HomeWelcome } from "./components/home/HomeWelcome";
import { HomeTutorialModal, type TutorialSlide } from "./components/home/HomeTutorialModal";
import { HomePoolList } from "./components/home/HomePoolList";
import { PlayoffInsightPanel } from "./components/home/PlayoffInsightPanel";
import { UnauthenticatedLandingPage } from "./components/guest/UnauthenticatedLandingPage";
import { PicksPage } from "./pages/PicksPage";
import { JoinPoolPage } from "./pages/JoinPoolPage";
import { SupportPage } from "./pages/SupportPage";
import { StandingsPage } from "./pages/StandingsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { useApiAuth } from "./hooks/useApiAuth";
import { usePortfolio } from "./hooks/usePortfolio";
import { usePools } from "./hooks/usePools";
import { useProfile } from "./hooks/useProfile";
import { usePoolJoin } from "./hooks/usePoolJoin";
import { useWizard } from "./hooks/useWizard";
import { useOrderForm } from "./hooks/useOrderForm";
import { useHomePoolSummary } from "./hooks/useHomePoolSummary";
import { useEngagementFeed } from "./hooks/useEngagementFeed";
import useMarket from "./hooks/useMarket";
import { usePoolOrderbookCoordinator } from "./hooks/usePoolOrderbookCoordinator";
import { useLazyDirtyRefreshOnFocus } from "./hooks/useLazyDirtyRefreshOnFocus";
import { CompetitionRoundsProvider } from "./hooks/useCompetitionRounds";
import {
  HOCKEY_TRADING_COMPETITION_CODE,
  isSoccerTradingCompetition,
} from "@sportex100/shared/utils/competitionIdentity.ts";
import { StatusMessage } from "./components/ui";
import { ChatWidget } from "./components/ui/ChatWidget";
import { apiUrl } from "./lib/api";
import { markDirty, subscribeDirtyViewCrossTab } from "./lib/dirtyViews";
import { useLocalization } from "./lib/i18n";
import { queryKeys } from "./lib/queryKeys";
import { useSeoMetadata } from "./lib/seo";
import { cn } from "./lib/utils";
import {
  buildWebOrderPromotionDraft,
  getPromotionTargetSide,
  type ChatComposerDraft,
  type ChatOrderPromotionAttachment,
} from "./lib/chatPromotions";
import type { PortfolioOrder } from "./types/portfolio";
import type { PoolMembership } from "./types/pools";

const HOME_TUTORIAL_ENABLED_KEY = "home.tutorial.enabled.v1";
const HOME_SOCCER_PROMO_COLLAPSED_KEY = "home.soccerPromo.collapsed.v1";
const LEGACY_SHOW_TIPS_STORAGE_KEY = "sportex100:showTips";
const PROFILE_SETTINGS_VERSION_LABEL = "SportEx100 Web v0.1.0";
const GUEST_LANDING_ROUTE_PATHS = new Set(["/", "/market", "/portfolio", "/research", "/standings"]);

const getApiErrorMessage = (value: unknown, fallback: string) => {
  if (value && typeof value === "object" && "detail" in value) {
    const detail = (value as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }
  return fallback;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { acquireToken } = useApiAuth();
  const queryClient = useQueryClient();
  const { locale, preference: languagePreference, t } = useLocalization();
  const [selectedPoolCode, setSelectedPoolCode] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    const stored = window.localStorage.getItem("selectedPoolCode");
    return stored;
  });
  const [homeTutorialEnabled, setHomeTutorialEnabled] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    const stored =
      window.localStorage.getItem(HOME_TUTORIAL_ENABLED_KEY) ??
      window.localStorage.getItem(LEGACY_SHOW_TIPS_STORAGE_KEY);
    return stored !== "0";
  });
  const [soccerPromoCollapsed, setSoccerPromoCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.localStorage.getItem(HOME_SOCCER_PROMO_COLLAPSED_KEY) === "1";
  });
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isHomeTutorialModalOpen, setIsHomeTutorialModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState<"sign-in" | "sign-up" | null>(null);
  const [marketFlashToken, setMarketFlashToken] = useState(0);
  const [isMarketFlashing, setIsMarketFlashing] = useState(false);
  const [chatComposerDraft, setChatComposerDraft] = useState<ChatComposerDraft | null>(null);
  const wasAuthenticatedRef = useRef<boolean | null>(null);
  const handledPoolReviewRequestRef = useRef<string | null>(null);
  const syncedLanguagePreferenceRef = useRef<string | null>(null);

  const { isLoaded: isUserLoaded, isSignedIn: isAuthenticated, user } = useUser();
  const { redirectToSignIn, signOut } = useClerk();

  useEffect(() => {
    if (!isAuthenticated) {
      syncedLanguagePreferenceRef.current = null;
      return;
    }
    const preference = languagePreference ?? "device";
    const syncKey = `${preference}:${locale}`;
    if (syncedLanguagePreferenceRef.current === syncKey) return;
    syncedLanguagePreferenceRef.current = syncKey;

    const syncLanguagePreference = async () => {
      const accessToken = await acquireToken();
      if (!accessToken) return;
      const response = await fetch(apiUrl("/api/profile/language"), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          language_preference: preference,
          notification_locale: locale,
        }),
      });
      if (!response.ok) {
        syncedLanguagePreferenceRef.current = null;
      }
    };

    void syncLanguagePreference();
  }, [acquireToken, isAuthenticated, languagePreference, locale]);

  // Auth handlers
  const handleLogin = useCallback(() => {
    setAuthModalOpen("sign-in");
  }, []);

  const handleSignUp = useCallback(() => {
    setAuthModalOpen("sign-up");
  }, []);

  const {
    memberships: pools,
    isLoading: isLoadingPools,
    isError: isPoolsError,
    reload: reloadPools,
  } = usePools({ enabled: !!isAuthenticated });

  const {
    profile,
    reload: reloadProfile,
  } = useProfile({ enabled: !!isAuthenticated });

  // Custom hooks for modals and forms
  const wizard = useWizard(reloadPools);
  const orderForm = useOrderForm();
  const poolJoin = usePoolJoin({
    onSuccess: (poolCode) => setSelectedPoolCode(poolCode),
    onReloadPools: reloadPools,
    onLogin: handleLogin,
  });
  const { resetFeedback } = poolJoin;

  const lastAccountIdRef = useRef<string | null>(null);
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "";
  const hasPools = pools.length > 0;
  const hasSoccerPoolMembership = useMemo(
    () => pools.some((pool) => isSoccerTradingCompetition(pool.competition_code)),
    [pools]
  );

  const activePool = useMemo(
    () => pools.find((pool) => pool.pool_code === selectedPoolCode) ?? pools[0] ?? null,
    [pools, selectedPoolCode]
  );
  useSeoMetadata(location.pathname, activePool);

  const competitionCode = activePool?.competition_code ?? HOCKEY_TRADING_COMPETITION_CODE;
  const seasonCode = activePool?.season_code ?? "2024";
  const homeIntelligenceEnabled = import.meta.env.VITE_HOME_INTELLIGENCE_V1 !== "0";
  const engagementFeedEnabled = import.meta.env.VITE_ENGAGEMENT_FEED_V1 !== "0";

  useEffect(() => {
    if (!hasSoccerPoolMembership || typeof window === "undefined") return;
    if (window.localStorage.getItem(HOME_SOCCER_PROMO_COLLAPSED_KEY) !== null) return;

    setSoccerPromoCollapsed(true);
    window.localStorage.setItem(HOME_SOCCER_PROMO_COLLAPSED_KEY, "1");
  }, [hasSoccerPoolMembership]);

  const handleSoccerPromoCollapsedChange = useCallback((collapsed: boolean) => {
    setSoccerPromoCollapsed(collapsed);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(HOME_SOCCER_PROMO_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, []);

  const standingsInput = useMemo(
    () => ({
      competitionCode,
      seasonCode,
      poolCode: activePool?.pool_code ?? "",
      enabled: Boolean(isAuthenticated && activePool),
    }),
    [activePool, competitionCode, isAuthenticated, seasonCode]
  );

  const {
    standings,
    isLoading: isLoadingStandings,
    isError: isStandingsError,
    reload: reloadStandings,
  } = useMarket(standingsInput);
  const {
    portfolio,
    isLoading: isLoadingPortfolio,
    isError: isPortfolioError,
    reload: reloadPortfolio,
    canLoad: canLoadPortfolio,
    cancelOrder,
    updateOrder,
  } = usePortfolio({
    poolCode: activePool?.pool_code ?? null,
    enabled: !!isAuthenticated,
  });
  const {
    byPoolCode: homeSummaryByPoolCode,
    reload: reloadHomeSummary,
    markRead: markHomeSummaryRead,
  } = useHomePoolSummary({
    enabled: !!isAuthenticated && homeIntelligenceEnabled,
  });
  const {
    items: engagementItems,
    reload: reloadEngagementFeed,
  } = useEngagementFeed({
    competitionCode: activePool?.competition_code ?? null,
    seasonCode: activePool?.season_code ?? null,
    poolCode: activePool?.pool_code ?? null,
    enabled:
      !!isAuthenticated &&
      engagementFeedEnabled &&
      !!activePool &&
      activePool.membership_status !== "PENDING",
  });
  const isPendingMembership = activePool?.membership_status === "PENDING";
  const pendingTradingLockedMessage = `${t("market.pending.title")}\n${t("market.pending.message")}`;
  const homeTutorialSlides = useMemo<TutorialSlide[]>(
    () => [
      {
        title: t("home.tutorial.step1.heading"),
        body: t("home.tutorial.step1.body"),
      },
      {
        title: t("home.tutorial.step2.heading"),
        body: t("home.tutorial.step2.body"),
      },
      {
        title: t("home.tutorial.step3.heading"),
        body: t("home.tutorial.step3.body"),
      },
      {
        title: t("home.tutorial.step4.heading"),
        body: `${t("home.tutorial.step4.body")}\n\n${t("home.tutorial.step4.example")}`,
      },
      {
        title: t("home.tutorial.step5.heading"),
        body: `${t("home.tutorial.step5.body")}\n\n${t("home.tutorial.step5.example")}`,
      },
      {
        title: t("home.tutorial.step6.heading"),
        body: t("home.tutorial.step6.body"),
      },
    ],
    [t],
  );

  // Derive active tab from current route
  const researchRefetchRef = useRef<(() => void) | null>(null);

  const activeTab = useMemo(() => {
    const path = location.pathname;
    if (path === "/market") return "MARKET";
    if (path === "/portfolio") return "PORTFOLIO";
    if (path === "/styleguide") return "STYLEGUIDE";
    return "HOME";
  }, [location.pathname]);

  const isMarketTab = activeTab === "MARKET";
  const isPortfolioTab = activeTab === "PORTFOLIO";
  const isHomeTab = activeTab === "HOME";
  const isStandingsTab = location.pathname === "/standings";
  const isResearchTab = location.pathname === "/research";
  
  // Show refresh button on home, market, portfolio, and research pages
  const showRefreshButton = isHomeTab || isMarketTab || isPortfolioTab || isResearchTab;

  const { latestOrderbookMessage } = usePoolOrderbookCoordinator({
    competitionCode: activePool?.competition_code ?? null,
    seasonCode: activePool?.season_code ?? null,
    poolCode: activePool?.pool_code ?? null,
    enabled: !!isAuthenticated,
  });

  useEffect(() => {
    const unsubscribe = subscribeDirtyViewCrossTab(queryClient);
    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  useEffect(() => {
    const currentAccountId = user?.id ?? null;
    if (currentAccountId && lastAccountIdRef.current !== currentAccountId) {
      const previousAccountId = lastAccountIdRef.current;
      lastAccountIdRef.current = currentAccountId;
      if (previousAccountId && previousAccountId !== currentAccountId) {
        queryClient.clear();
        setSelectedPoolCode(null);
      }
    }
  }, [queryClient, user]);

  useEffect(() => {
    // Only clear selectedPoolCode if user logs out (was authenticated, now isn't)
    // Don't clear on initial mount when wasAuthenticatedRef is null
    if (wasAuthenticatedRef.current === true && !isAuthenticated) {
      setSelectedPoolCode(null);
    }
    wasAuthenticatedRef.current = !!isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isUserLoaded || !isAuthenticated) {
      return;
    }

    const pendingCreatePoolIntent = sessionStorage.getItem("pendingCreatePoolIntent");
    if (pendingCreatePoolIntent !== "1") {
      return;
    }

    sessionStorage.removeItem("pendingCreatePoolIntent");
    setAuthModalOpen(null);
    wizard.open("create");
  }, [isAuthenticated, isUserLoaded, wizard.open]);

  useEffect(() => {
    if (hasPools) {
      resetFeedback();
    }
  }, [hasPools, resetFeedback]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reviewTarget = params.get("review");
    const requestedPoolCode = params.get("pool_code");
    if (reviewTarget !== "members" || !requestedPoolCode) {
      return;
    }

    if (!isAuthenticated) {
      setAuthModalOpen("sign-in");
      return;
    }

    const requestKey = `${requestedPoolCode.toUpperCase()}:${reviewTarget}`;
    if (handledPoolReviewRequestRef.current === requestKey || pools.length === 0) {
      return;
    }

    const targetPool = pools.find((pool) => pool.pool_code.toUpperCase() === requestedPoolCode.toUpperCase());
    if (!targetPool) {
      return;
    }

    handledPoolReviewRequestRef.current = requestKey;
    setSelectedPoolCode(targetPool.pool_code);
    if (targetPool.is_admin) {
      wizard.open("edit", targetPool);
    }

    params.delete("pool_code");
    params.delete("review");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [isAuthenticated, location.pathname, location.search, navigate, pools, wizard.open]);

  useEffect(() => {
    if (!marketFlashToken) {
      return;
    }
    setIsMarketFlashing(true);
    const timeoutId = window.setTimeout(() => setIsMarketFlashing(false), 400);
    return () => clearTimeout(timeoutId);
  }, [marketFlashToken]);

  // Handle pending pool code from invitation link (/join page)
  const hasPromptedAuthRef = useRef(false);
  const redeemingInviteTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isUserLoaded) {
      return;
    }

    if (!isAuthenticated) {
      const pendingInviteToken = sessionStorage.getItem("pendingInviteToken");
      const pendingCode = sessionStorage.getItem("pendingPoolCode");
      const pendingCodeAuthSource = sessionStorage.getItem("pendingPoolCodeAuthSource");
      const shouldPromptForPendingCode = Boolean(pendingCode && pendingCodeAuthSource === "join_link");
      if ((pendingInviteToken || shouldPromptForPendingCode) && !hasPromptedAuthRef.current) {
        hasPromptedAuthRef.current = true;
        setAuthModalOpen("sign-in");
      }
      return;
    }

    const pendingInviteToken = sessionStorage.getItem("pendingInviteToken");
    const pendingCode = sessionStorage.getItem("pendingPoolCode");
    if (pendingInviteToken) {
      const requiresFreshAuth = sessionStorage.getItem("pendingInviteRequiresFreshAuth") === "1";
      const startedSignedOut = sessionStorage.getItem("pendingInviteStartedSignedOut") === pendingInviteToken;
      if (requiresFreshAuth && !startedSignedOut) {
        sessionStorage.removeItem("pendingInviteRequiresFreshAuth");
        sessionStorage.removeItem("pendingInviteStartedSignedOut");
        setAuthModalOpen("sign-in");
        void (async () => {
          await signOut();
        })();
        return;
      }

      if (redeemingInviteTokenRef.current === pendingInviteToken) {
        return;
      }
      redeemingInviteTokenRef.current = pendingInviteToken;

      const redeemPendingInvite = async () => {
        try {
          const accessToken = await acquireToken();
          if (!accessToken) {
            redeemingInviteTokenRef.current = null;
            setAuthModalOpen("sign-in");
            return;
          }

          const response = await fetch(apiUrl("/api/pools/invites/redeem"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ inviteToken: pendingInviteToken }),
          });

          if (!response.ok) {
            if (pendingCode) {
              poolJoin.setJoinPoolCode(pendingCode);
            }
            sessionStorage.removeItem("pendingInviteToken");
            sessionStorage.removeItem("pendingInviteRequiresFreshAuth");
            sessionStorage.removeItem("pendingInviteStartedSignedOut");
            redeemingInviteTokenRef.current = null;
            console.error("Failed to redeem pool invite", response.status);
            return;
          }

          const membership = (await response.json()) as PoolMembership;
          if (user?.id) {
            queryClient.setQueryData<PoolMembership[]>(["pools", user?.id], (previous = []) => [
              membership,
              ...previous.filter((pool) => pool.pool_code !== membership.pool_code),
            ]);
          }
          setSelectedPoolCode(membership.pool_code);
          sessionStorage.removeItem("pendingInviteToken");
          sessionStorage.removeItem("pendingPoolCode");
          sessionStorage.removeItem("pendingPoolCodeAuthSource");
          sessionStorage.removeItem("pendingInviteRequiresFreshAuth");
          sessionStorage.removeItem("pendingInviteStartedSignedOut");
          await reloadPools();
          setAuthModalOpen(null);
          hasPromptedAuthRef.current = false;
        } catch (error) {
          console.error("Failed to redeem pool invite", error);
          redeemingInviteTokenRef.current = null;
        }
      };

      void redeemPendingInvite();
      return;
    }

    if (pendingCode && pools.length > 0) {
      const matchingPool = pools.find(p => p.pool_code.toUpperCase() === pendingCode.toUpperCase());
      if (matchingPool) {
        setSelectedPoolCode(matchingPool.pool_code);
        sessionStorage.removeItem("pendingPoolCode");
        sessionStorage.removeItem("pendingPoolCodeAuthSource");
      } else {
        poolJoin.setJoinPoolCode(pendingCode);
        sessionStorage.removeItem("pendingPoolCode");
        sessionStorage.removeItem("pendingPoolCodeAuthSource");
      }
      hasPromptedAuthRef.current = false;
    }
  }, [acquireToken, isAuthenticated, isUserLoaded, location.pathname, location.search, pools, poolJoin, queryClient, reloadPools, signOut, user?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (selectedPoolCode) {
      window.localStorage.setItem("selectedPoolCode", selectedPoolCode);
    } else {
      window.localStorage.removeItem("selectedPoolCode");
    }
  }, [selectedPoolCode]);

  const previousPoolCodeRef = useRef<string | null>(null);
  useEffect(() => {
    const currentPoolCode = activePool?.pool_code ?? null;
    if (!currentPoolCode) {
      previousPoolCodeRef.current = null;
      return;
    }
    if (previousPoolCodeRef.current && previousPoolCodeRef.current !== currentPoolCode) {
      queryClient.removeQueries({ queryKey: ["orderbook-latest"] });
      queryClient.removeQueries({ queryKey: ["dirty-view"] });
    }
    previousPoolCodeRef.current = currentPoolCode;
  }, [activePool?.pool_code, queryClient]);

  const handleOpenOrderForm = useCallback(
    (prefill?: { side: "BUY" | "SELL"; teamCode: string | null; price?: number; quantity?: number }) => {
      if (!isAuthenticated) {
        redirectToSignIn();
        return;
      }
      if (!activePool) {
        return;
      }
      if (activePool.membership_status === "PENDING") {
        window.alert(pendingTradingLockedMessage);
        return;
      }
      orderForm.open(prefill?.side ?? "BUY", prefill?.teamCode ?? "", prefill?.price, prefill?.quantity);
    },
    [activePool, pendingTradingLockedMessage, redirectToSignIn, isAuthenticated, orderForm],
  );

  const handleSelectMarket = useCallback(
    ({ teamCode, side, price }: { teamCode: string; side: "BUY" | "SELL"; price?: number }) => {
      handleOpenOrderForm({ teamCode, side, price });
    },
    [handleOpenOrderForm],
  );

  const handleEditOrder = useCallback(
    (order: PortfolioOrder) => {
      if (!isAuthenticated || !activePool) {
        return;
      }
      if (activePool.membership_status === "PENDING") {
        window.alert(pendingTradingLockedMessage);
        return;
      }
      // Open order form pre-filled with order values (using open_quantity for remaining amount)
      // The cancel will happen when the user submits the new order
      orderForm.open(order.side, order.team_code, order.price, order.open_quantity, order.trade_order_id);
    },
    [isAuthenticated, activePool, orderForm, pendingTradingLockedMessage],
  );

  const handlePromoteOrder = useCallback((order: PortfolioOrder) => {
    if (!isAuthenticated || !activePool) {
      return;
    }
    setChatComposerDraft(buildWebOrderPromotionDraft(order));
  }, [activePool, isAuthenticated]);

  const handleSelectPool = useCallback((poolCode: string) => {
    setSelectedPoolCode(poolCode);
  }, []);

  const navigateToTab = useCallback(
    (tab: "market" | "portfolio" | "picks" | "standings" | "home") => {
      if (tab === "market") {
        navigate("/market");
      } else if (tab === "portfolio") {
        navigate("/portfolio");
      } else if (tab === "picks") {
        navigate("/research");
      } else if (tab === "standings") {
        navigate("/standings");
      } else {
        navigate("/");
      }
    },
    [navigate]
  );

  const handleOpenPromotedOrder = useCallback(
    (attachment: ChatOrderPromotionAttachment) => {
      navigateToTab("market");
      handleOpenOrderForm({
        side: getPromotionTargetSide(attachment.side),
        teamCode: attachment.teamCode,
        price: attachment.price,
        quantity: attachment.quantity,
      });
    },
    [handleOpenOrderForm, navigateToTab],
  );

  const handleSaveProfile = useCallback(async (traderCode: string) => {
    const token = await acquireToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(apiUrl("/api/profile"), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ trader_code: traderCode }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Failed to update profile" }));
      throw new Error(error.detail || "Failed to update profile");
    }

    // Reload pools and profile to get updated trader code
    await Promise.all([reloadPools(), reloadProfile()]);
  }, [acquireToken, reloadPools, reloadProfile]);

  const handleHomeTutorialEnabledChange = useCallback((nextEnabled: boolean) => {
    setHomeTutorialEnabled(nextEnabled);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(HOME_TUTORIAL_ENABLED_KEY, nextEnabled ? "1" : "0");
      window.localStorage.setItem(LEGACY_SHOW_TIPS_STORAGE_KEY, nextEnabled ? "1" : "0");
    }
  }, []);

  const handleResetTips = useCallback(() => {
    handleHomeTutorialEnabledChange(true);
    setIsEditProfileOpen(false);
    setIsHomeTutorialModalOpen(true);
  }, [handleHomeTutorialEnabledChange]);

  const homeTutorialInlinePrompt = homeTutorialEnabled ? (
    <button
      type="button"
      onClick={() => setIsHomeTutorialModalOpen(true)}
      className="flex min-w-0 items-center gap-3 rounded-[10px] border border-brand/40 bg-brand/10 px-[14px] py-2 text-left transition hover:border-brand/70 hover:bg-brand/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <span className="shrink-0 rounded-[6px] bg-brand px-2 py-1 font-display text-[11px] font-bold uppercase leading-none text-black">
        {t("home.tutorial.stepLabel", { step: 1, total: homeTutorialSlides.length })}
      </span>
      <span className="min-w-0 truncate text-[13px] font-semibold leading-[18px] text-foreground">
        {homeTutorialSlides[0]?.title ?? t("home.tutorial.modalTitle")}
      </span>
    </button>
  ) : null;

  const handleUploadAvatar = useCallback(async (file: File) => {
    if (!user) {
      throw new Error("Authentication required");
    }

    const token = await acquireToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const image = await user.setProfileImage({ file });
    await user.reload();
    const avatarUrl = image.publicUrl ?? user.imageUrl;

    const response = await fetch(apiUrl("/api/profile/avatar"), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ avatar_url: avatarUrl }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(getApiErrorMessage(error, "Failed to update avatar"));
    }

    await reloadProfile();
  }, [acquireToken, reloadProfile, user]);

  const handleOpenPrivacy = useCallback(() => {
    setIsEditProfileOpen(false);
    navigate("/privacy");
  }, [navigate]);

  const handleOpenSupport = useCallback(() => {
    setIsEditProfileOpen(false);
    navigate("/support");
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    setIsEditProfileOpen(false);
    await signOut();
    navigate("/");
  }, [navigate, signOut]);

  const handleDeleteAccount = useCallback(async () => {
    if (!user) {
      throw new Error("Authentication required");
    }
    if (!user.deleteSelfEnabled) {
      throw new Error("Account deletion is not enabled for this account");
    }

    const token = await acquireToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(apiUrl("/api/profile"), {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(getApiErrorMessage(error, "Failed to delete account"));
    }

    window.localStorage.removeItem("selectedPoolCode");
    window.localStorage.removeItem(HOME_TUTORIAL_ENABLED_KEY);
    window.localStorage.removeItem(LEGACY_SHOW_TIPS_STORAGE_KEY);
    setIsEditProfileOpen(false);
    await user.delete();
    navigate("/");
  }, [acquireToken, navigate, user]);

  const reloadResearch = useCallback(() => {
    if (researchRefetchRef.current) {
      researchRefetchRef.current();
    }
  }, []);

  const reloadHome = useCallback(() => {
    void reloadPools();
    void reloadHomeSummary();
    void reloadEngagementFeed();
  }, [reloadEngagementFeed, reloadHomeSummary, reloadPools]);

  const lazyRefetchHome = useCallback(async () => {
    const poolsResult = await reloadPools();
    const summaryResult = await reloadHomeSummary();
    const feedResult = await reloadEngagementFeed();

    return {
      isError:
        ((poolsResult as { isError?: boolean } | undefined)?.isError ?? false) ||
        ((summaryResult as { isError?: boolean } | undefined)?.isError ?? false) ||
        ((feedResult as { isError?: boolean } | undefined)?.isError ?? false),
    };
  }, [reloadEngagementFeed, reloadHomeSummary, reloadPools]);

  useLazyDirtyRefreshOnFocus("home", activePool?.pool_code ?? null, isHomeTab, lazyRefetchHome);
  useLazyDirtyRefreshOnFocus("portfolio", activePool?.pool_code ?? null, isPortfolioTab, async () => {
    const result = await reloadPortfolio();
    return result;
  });

  const isPublicJoinRoute = location.pathname.startsWith("/join/");
  const isGuestLandingRoute = isPublicJoinRoute || (!isAuthenticated && GUEST_LANDING_ROUTE_PATHS.has(location.pathname));

  return (
    <CompetitionRoundsProvider competitionCode={competitionCode}>
      <div className={cn("min-h-screen", isGuestLandingRoute ? "bg-black" : "bg-[#0B0F14] lg:px-6 xl:px-8")}>
      <div
        className={cn(
          "flex min-h-screen flex-col",
          isGuestLandingRoute
            ? "w-full bg-black"
            : "mx-auto max-w-[64rem] gap-2 bg-background px-1 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-0 shadow-[0_0_40px_rgba(0,0,0,0.28)] md:gap-4 md:px-7xl lg:pb-4",
        )}
      >
        {!isGuestLandingRoute && (
          <AppHeader
            isAuthenticated={!!isAuthenticated}
            displayName={displayName}
            traderCode={profile?.trader_code}
            avatarUrl={profile?.avatar_url ?? user?.imageUrl ?? null}
            activePool={activePool}
            hasPools={hasPools}
            onLogin={handleLogin}
            onEditProfile={() => setIsEditProfileOpen(true)}
            onRefreshStandings={reloadStandings}
            onRefreshPortfolio={reloadPortfolio}
            onRefreshPools={reloadHome}
            onRefreshResearch={reloadResearch}
            showRefreshButton={showRefreshButton}
          />
        )}

        <main className={cn("min-w-0 flex-1", isGuestLandingRoute && "bg-black")}>
          <Routes>
          <Route
            path="/"
            element={
              !isAuthenticated ? (
                <UnauthenticatedLandingPage onSignUp={handleSignUp} onSignIn={handleLogin} />
              ) : (
                <div className="mx-auto flex w-full min-w-0 max-w-[64rem] flex-col gap-4 overflow-x-hidden md:gap-6">
                  <HomeWelcome
                    isAuthenticated={!!isAuthenticated}
                    hasPools={hasPools}
                    onLogin={handleLogin}
                    onSignUp={handleSignUp}
                    joinPoolCode={poolJoin.joinPoolCode}
                    isJoiningPool={poolJoin.isJoiningPool}
                    joinPoolFeedback={poolJoin.joinPoolFeedback}
                    joinPoolFeedbackKind={poolJoin.joinPoolFeedbackKind}
                    onJoinPoolCodeChange={poolJoin.handleJoinPoolCodeChange}
                    onJoinPoolSubmit={poolJoin.handleJoinPoolSubmit}
                    onCreatePool={() => wizard.open('create')}
                    soccerPromoCollapsed={soccerPromoCollapsed}
                    onSoccerPromoCollapsedChange={handleSoccerPromoCollapsedChange}
                  />

                  {isAuthenticated && (
                    <HomePoolList
                      pools={pools}
                      summaryByPoolCode={homeSummaryByPoolCode}
                      joinPoolCode={poolJoin.joinPoolCode}
                      isJoiningPool={poolJoin.isJoiningPool}
                      joinPoolFeedback={poolJoin.joinPoolFeedback}
                      joinPoolFeedbackKind={poolJoin.joinPoolFeedbackKind}
                      onJoinPoolCodeChange={poolJoin.handleJoinPoolCodeChange}
                      onJoinPoolSubmit={poolJoin.handleJoinPoolSubmit}
                      onCreatePool={() => wizard.open('create')}
                      activePoolCode={activePool?.pool_code}
                      currentTraderCode={profile?.trader_code ?? activePool?.trader_code ?? null}
                      isLoading={isLoadingPools}
                      isError={isPoolsError}
                      onSelectPool={handleSelectPool}
                      onManagePool={(pool) => wizard.open('edit', pool)}
                      onMarkRead={markHomeSummaryRead}
                      tutorialSlot={homeTutorialInlinePrompt}
                      pulseSlot={<PlayoffInsightPanel items={engagementItems} />}
                    />
                  )}
                </div>
              )
            }
          />

          <Route
            path="/market"
            element={
              <div
                className={`mx-auto flex w-full max-w-[64rem] flex-col gap-4 ${
                  isMarketFlashing ? "market-flash" : ""
                }`}
              >
                {!isAuthenticated && <Navigate to={{ pathname: "/", hash: "#market" }} replace />}
                {isAuthenticated && !hasPools && (
                  <StatusMessage>{t("market.noPool.message")}</StatusMessage>
                )}
                {isAuthenticated && hasPools && isLoadingStandings && (
                  <StatusMessage>{t("home.feed.loading")}</StatusMessage>
                )}
                {isAuthenticated && hasPools && isStandingsError && (
                  <StatusMessage variant="error">
                    {t("market.error.loadFailed")}
                  </StatusMessage>
                )}
                {isAuthenticated && hasPools && !isLoadingStandings && !isStandingsError && (
                  <MarketTable
                    standings={standings}
                    onSelectOrder={handleSelectMarket}
                    competitionCode={competitionCode}
                    seasonCode={seasonCode}
                    poolCode={activePool?.pool_code ?? ""}
                    orderbookMessage={latestOrderbookMessage}
                  />
                )}
              </div>
            }
          />

          <Route
            path="/portfolio"
            element={
              <div className="mx-auto flex w-full max-w-[64rem] flex-col gap-4">
                {!isAuthenticated && <Navigate to={{ pathname: "/", hash: "#track" }} replace />}
                {isAuthenticated && !hasPools && (
                  <StatusMessage>{t("portfolio.noPool.message")}</StatusMessage>
                )}
                {isAuthenticated && hasPools && (
                  <PortfolioView
                    data={portfolio}
                    competitionCode={competitionCode}
                    isLoading={isLoadingPortfolio && canLoadPortfolio}
                    isError={isPortfolioError}
                    onCancelOrder={cancelOrder}
                    onEditOrder={handleEditOrder}
                    onPromoteOrder={handlePromoteOrder}
                  />
                )}
              </div>
            }
          />

          <Route
            path="/research"
            element={
              <>
                {!isAuthenticated && <Navigate to={{ pathname: "/", hash: "#picks" }} replace />}
                {isAuthenticated && (
                  <PicksPage 
                    competitionCode={competitionCode} 
                    seasonCode={seasonCode}
                    poolCode={activePool?.pool_code ?? null}
                    onRefresh={(fn) => { researchRefetchRef.current = fn; }}
                    onOpenOrderForm={handleOpenOrderForm}
                  />
                )}
              </>
            }
          />

          <Route path="/join" element={<JoinPoolPage />} />
          <Route path="/join/:poolCode" element={<JoinPoolPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          <Route
            path="/standings"
            element={
              <div className="mx-auto flex w-full max-w-[64rem] flex-col gap-4 md:gap-6">
                {!isAuthenticated && <Navigate to={{ pathname: "/", hash: "#track" }} replace />}
                {isAuthenticated && !hasPools && (
                  <StatusMessage>{t("standings.noPool.message")}</StatusMessage>
                )}
                {isAuthenticated && hasPools && (
                  <StandingsPage
                    poolCode={activePool?.pool_code ?? null}
                    isActive={isStandingsTab}
                    currentTraderCode={profile?.trader_code ?? activePool?.trader_code ?? null}
                  />
                )}
              </div>
            }
          />

          <Route path="/styleguide" element={<StyleGuide />} />
          </Routes>
        </main>
        {!isGuestLandingRoute && <AppFooter />}

      {orderForm.isOpen && activePool && (
        <OrderTicket
          competitionCode={competitionCode}
          seasonCode={seasonCode}
          poolCode={activePool.pool_code}
          traderCode={activePool.trader_code}
          standings={standings}
          initialSide={orderForm.prefill.side}
          initialTeamCode={orderForm.prefill.teamCode}
          initialPrice={orderForm.prefill.price}
          initialQuantity={orderForm.prefill.quantity}
          editingOrderId={orderForm.prefill.editingOrderId}
          tradingLocked={isPendingMembership}
          onUpdateOrder={updateOrder}
          onClose={orderForm.close}
          onOrderBooked={() => {
            setMarketFlashToken(Date.now());
            if (activePool?.pool_code) {
              markDirty(queryClient, "portfolio", activePool.pool_code, "order_submit");
              markDirty(queryClient, "leaderboard", activePool.pool_code, "order_submit");
              markDirty(queryClient, "home", activePool.pool_code, "order_submit");
              queryClient.invalidateQueries({
                queryKey: queryKeys.market(competitionCode, seasonCode, activePool.pool_code),
                refetchType: "none",
              });
            }
          }}
        />
      )}

      {wizard.isOpen && (
        <PoolWizard
          mode={wizard.mode}
          pool={wizard.pool}
          onClose={wizard.close}
        />
      )}

      {isEditProfileOpen && profile && (
        <EditProfileModal
          isOpen={isEditProfileOpen}
          onClose={() => setIsEditProfileOpen(false)}
          currentTraderCode={profile.trader_code}
          displayName={displayName}
          avatarUrl={profile?.avatar_url ?? user?.imageUrl ?? null}
          versionLabel={PROFILE_SETTINGS_VERSION_LABEL}
          onSave={handleSaveProfile}
          homeTutorialEnabled={homeTutorialEnabled}
          onHomeTutorialEnabledChange={handleHomeTutorialEnabledChange}
          onResetTips={handleResetTips}
          onUploadAvatar={handleUploadAvatar}
          onOpenPrivacy={handleOpenPrivacy}
          onOpenSupport={handleOpenSupport}
          onSignOut={handleSignOut}
          onDeleteAccount={handleDeleteAccount}
        />
      )}

      <HomeTutorialModal
        isOpen={homeTutorialEnabled && isHomeTutorialModalOpen}
        onClose={() => setIsHomeTutorialModalOpen(false)}
        slides={homeTutorialSlides}
        hideImages
      />

      {authModalOpen && (
        <AuthModal
          isOpen={true}
          onClose={() => setAuthModalOpen(null)}
          mode={authModalOpen}
          onModeChange={(newMode) => setAuthModalOpen(newMode)}
        />
      )}

      {!isGuestLandingRoute && (
        <ChatWidget
          poolCode={activePool?.pool_code ?? null}
          poolName={activePool?.pool_name ?? null}
          traderCode={activePool?.trader_code ?? null}
          displayName={displayName}
          isAuthenticated={!!isAuthenticated}
          composerDraft={chatComposerDraft}
          onComposerDraftConsumed={() => setChatComposerDraft(null)}
          onOpenPromotedOrder={handleOpenPromotedOrder}
        />
      )}

      </div>
      </div>
    </CompetitionRoundsProvider>
  );
}

export default App;
